import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { PanelRightIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import {
  useLocation,
  useNavigate,
  useOutletContext,
  useParams
} from 'react-router';

import { ContactAddressesContent } from '@/components/contact-detail/addresses/ContactAddressesContent';
import { ContactFintrxContent } from '@/components/contact-detail/fintrx/ContactFintrxContent';
import { ContactLpPlatformContent } from '@/components/contact-detail/lp-platform/ContactLpPlatformContent';
import { ContactMarketingContent } from '@/components/contact-detail/marketing/ContactMarketingContent';
import { ContactMerrillLynchContent } from '@/components/contact-detail/merrill-lynch/ContactMerrillLynchContent';
import { contactNavItems } from '@/components/contact-detail/nav/constants';
import { ContactDetailNav } from '@/components/contact-detail/nav/ContactDetailNav';
import { ContactOthersContent } from '@/components/contact-detail/others/ContactOthersContent';
import { ContactRightPanel } from '@/components/contact-detail/panels/ContactRightPanel';
import { ContactSummaryContent } from '@/components/contact-detail/summary/ContactSummaryContent';
import type { AdditionalBreadcrumbItem } from '@/components/Layout';
import { cn } from '@/lib/utils';

const GET_CONTACT_DETAIL_HEADER = gql`
  query GetContactDetailHeader($id: ID!) {
    node(id: $id) {
      ... on ContactNode {
        id
        name
      }
    }
  }
`;

interface ContactDetailHeaderNode {
  id: string;
  name: string;
}

interface GetContactDetailHeaderData {
  node: ContactDetailHeaderNode | null;
}

interface GetContactDetailHeaderVariables {
  id: string;
}

export default function ContactDetailPage() {
  const { '*': tabPath, id } = useParams<{ '*': string; id: string }>();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { addBreadcrumb, clearBreadcrumbs } = useOutletContext<{
    addBreadcrumb?: (item: AdditionalBreadcrumbItem) => void;
    clearBreadcrumbs?: () => void;
  }>();
  const [, setActiveTab] = useState('summary');
  const [rightPanelOpen, setRightPanelOpen] = useState(true);
  const { data: contactHeaderData } = useQuery<
    GetContactDetailHeaderData,
    GetContactDetailHeaderVariables
  >(GET_CONTACT_DETAIL_HEADER, {
    skip: !id,
    variables: { id: id ?? '' }
  });
  const contactName = contactHeaderData?.node?.name?.trim() || '-';
  const validTabs = useMemo(() => contactNavItems.map(item => item.id), []);
  const tabFromUrl = tabPath?.split('/')[0] ?? '';
  const resolvedActiveTab = validTabs.includes(
    tabFromUrl as (typeof validTabs)[number]
  )
    ? tabFromUrl
    : 'summary';
  const basePath = `/firm/crm-contacts/${id ?? ''}`;

  useEffect(() => {
    if (!id) {
      return;
    }
    if (
      !tabFromUrl ||
      !validTabs.includes(tabFromUrl as (typeof validTabs)[number])
    ) {
      navigate(`${basePath}/summary`, { replace: true });
    }
  }, [basePath, id, navigate, tabFromUrl, validTabs]);

  useEffect(() => {
    if (!addBreadcrumb || contactName === '-') {
      return;
    }

    clearBreadcrumbs?.();
    addBreadcrumb({ label: contactName });

    return () => {
      clearBreadcrumbs?.();
    };
  }, [addBreadcrumb, clearBreadcrumbs, contactName, pathname]);

  return (
    <div
      className={cn(
        'flex h-[calc(100dvh-64px)] flex-col',
        rightPanelOpen
          ? 'md:grid md:grid-cols-[210px_1fr_auto]'
          : 'md:grid md:grid-cols-[210px_1fr_70px]'
      )}
    >
      {/* Contact Detail Sidebar */}
      <ContactDetailNav
        activeTab={resolvedActiveTab}
        basePath={basePath}
        items={contactNavItems}
        onTabChange={setActiveTab}
      />

      {/* Main Content Area */}
      <div className="min-h-0 flex-1 overflow-auto bg-white dark:bg-gray-950">
        <div className="bg-sidebar sticky top-0 z-1 flex h-12.25 items-center border-b px-5 py-1.5 md:pl-14">
          <div className="text-lg font-semibold">{contactName}</div>
        </div>
        <div className="p-5">
          {resolvedActiveTab === 'summary' && (
            <ContactSummaryContent contactId={id ?? ''} />
          )}
          {resolvedActiveTab === 'addresses' && (
            <ContactAddressesContent contactId={id ?? ''} />
          )}
          {resolvedActiveTab === 'marketing' && (
            <ContactMarketingContent contactId={id ?? ''} />
          )}
          {resolvedActiveTab === 'lp-platform' && (
            <ContactLpPlatformContent contactId={id ?? ''} />
          )}
          {resolvedActiveTab === 'fintrx' && (
            <ContactFintrxContent contactId={id ?? ''} />
          )}
          {resolvedActiveTab === 'merrill-lynch-data' && (
            <ContactMerrillLynchContent contactId={id ?? ''} />
          )}
          {resolvedActiveTab === 'others' && (
            <ContactOthersContent contactId={id ?? ''} />
          )}
          {resolvedActiveTab !== 'summary' &&
            resolvedActiveTab !== 'addresses' &&
            resolvedActiveTab !== 'marketing' &&
            resolvedActiveTab !== 'lp-platform' &&
            resolvedActiveTab !== 'fintrx' &&
            resolvedActiveTab !== 'merrill-lynch-data' &&
            resolvedActiveTab !== 'others' && (
              <div className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
                <p className="text-gray-600 dark:text-gray-400">
                  Content for {resolvedActiveTab} (contact {id ?? '—'}) tab will
                  be displayed here
                </p>
              </div>
            )}
        </div>
      </div>

      {/* Right Panel or Toggle Button */}
      <div className="hidden md:contents">
        {rightPanelOpen ? (
          <ContactRightPanel
            contactId={id ?? ''}
            isOpen={rightPanelOpen}
            onClose={() => {
              setRightPanelOpen(false);
            }}
          />
        ) : (
          <button
            className="bg-card sticky top-5 grid size-12.5 place-content-center rounded-2xl border-l shadow-lg"
            onClick={() => {
              setRightPanelOpen(true);
            }}
            title="Open panel"
            type="button"
          >
            <PanelRightIcon className="h-6 w-6 text-gray-600 dark:text-gray-400" />
          </button>
        )}
      </div>
    </div>
  );
}
